/**
 * Las recetas del paciente, escritas para que las entienda él.
 *
 * Cada medicamento dice cómo tomarlo en una sola línea —"1 tableta cada 8
 * horas por 3 días"— porque es exactamente lo que se olvida al salir del
 * consultorio, y dice si puede retirarlo en la farmacia del policlínico o
 * tiene que comprarlo fuera.
 */
import { useQuery } from "@tanstack/react-query";
import { Download, Pill } from "lucide-react";
import {
  type RecetaPortal,
  descargarReceta,
  descargarRecetaFirmada,
  misRecetas,
} from "../../api/portal";
import { usePortalAuth } from "../../auth/PortalAuthProvider";
import { useZonaHoraria } from "../../auth/TenantBrandingProvider";
import { fechaHora } from "../../lib/fecha";
import { Badge, Card, CardBody } from "../../components/ui";

const ESTADO: Record<string, { texto: string; variante: "success" | "warning" | "neutral" }> = {
  PENDIENTE: { texto: "Sin retirar", variante: "warning" },
  PARCIAL: { texto: "Retirada en parte", variante: "warning" },
  DISPENSADA: { texto: "Retirada", variante: "success" },
  NO_RETIRADA: { texto: "No retirada", variante: "neutral" },
};

export function PortalRecetasPage() {
  const { me } = usePortalAuth();
  const tz = useZonaHoraria();
  const { data: recetas = [], isLoading } = useQuery({
    queryKey: ["portal", "recetas"],
    queryFn: misRecetas,
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Mis recetas</h1>
        <p className="mt-0.5 text-sm text-slate-500">
          Lo que te indicaron en consulta, del último año.
        </p>
      </div>

      {isLoading && <p className="text-sm text-slate-400">Cargando…</p>}

      {!isLoading && recetas.length === 0 && (
        <Card>
          <CardBody className="py-10 text-center text-sm text-slate-500">
            Todavía no tienes recetas.
          </CardBody>
        </Card>
      )}

      {recetas.map((r) => (
        <RecetaCard
          key={r.id}
          receta={r}
          tz={tz}
          esOtro={r.paciente_id !== me?.yo.paciente_id}
        />
      ))}
    </div>
  );
}

function RecetaCard({
  receta: r,
  tz,
  esOtro,
}: {
  receta: RecetaPortal;
  tz: string;
  esOtro: boolean;
}) {
  const estado = ESTADO[r.estado] ?? { texto: r.estado, variante: "neutral" as const };
  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Pill className="h-4 w-4 text-[color:var(--color-link)]" />
          <span className="font-semibold text-slate-900">
            {fechaHora(r.fecha, tz)}
          </span>
          <Badge variant={estado.variante}>{estado.texto}</Badge>
          {esOtro && <Badge variant="neutral">{r.paciente_nombre}</Badge>}
          {r.profesional && (
            <span className="text-sm text-slate-500">· {r.profesional}</span>
          )}
          <button
            type="button"
            onClick={() => descargarReceta(r.id)}
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-[color:var(--color-link)] hover:bg-[color-mix(in_srgb,var(--color-link)_8%,white)]"
          >
            <Download className="h-4 w-4" />
            Descargar
          </button>
          {/* La firmada solo aparece si el médico la subió: es la que piden
              algunas farmacias para los medicamentos que no son de venta
              libre. */}
          {r.tiene_firma && (
            <button
              type="button"
              onClick={() => descargarRecetaFirmada(r.id)}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50"
            >
              <Download className="h-4 w-4" />
              Con firma
            </button>
          )}
        </div>

        <ul className="space-y-2">
          {r.items.map((i, idx) => (
            <li key={idx} className="rounded-lg border border-slate-100 px-3 py-2">
              <p className="font-medium text-slate-900">{i.descripcion}</p>
              <p className="text-sm text-slate-600">
                {[i.dosis, i.frecuencia, i.duracion].filter(Boolean).join(" · ") ||
                  "Según indicación médica"}
              </p>
              <p className="mt-0.5 text-xs">
                {i.en_farmacia ? (
                  <span className="text-emerald-700">
                    Puedes retirarlo en la farmacia del policlínico
                  </span>
                ) : (
                  <span className="text-amber-700">
                    Cómpralo en una farmacia
                  </span>
                )}
                <span className="text-slate-400"> · cantidad: {i.cantidad}</span>
              </p>
            </li>
          ))}
        </ul>

        {r.indicaciones && (
          <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
            {r.indicaciones}
          </p>
        )}
      </CardBody>
    </Card>
  );
}
